import { Box, Button, Chip, IconButton, Typography } from '@mui/joy'
import { CallStatus } from '@numeo/types'
import { getStatusColor } from '../../../utils/styles'
import { AutoAwesome, CallEnd, Mic, MicOff, OpenInFull, Route } from '@mui/icons-material'
import { CallDurationTimer } from '../../widget-subcomponents'
import { formatCurrency, formatLoadLocation, formatRatePerMile } from 'utils/formatters'
import { HorizontalDirectionIcon } from 'assets/icons/DatDirectionIcons'
import { START_BOTTOM, WIDGET_HEIGHT, WIDGET_SPACING } from '../../../const'
import { useAppSelector } from 'hooks'

interface MinimizedUserCallWidgetProps {
    callId: string
    isTransitioning: boolean
    onMaximize: () => void
    onToggleMute: () => void
    onEndCall: () => void
    muted: boolean
    index: number
    endingCall: boolean
}
export const MinimizedUserCallWidget = ({
    callId,
    onMaximize,
    isTransitioning,
    onToggleMute,
    onEndCall,
    muted,
    index,
    endingCall,
}: MinimizedUserCallWidgetProps) => {
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeCallsCount = Object.values(activeAvailableCalls).length
    const activeAvailableCall = activeAvailableCalls[callId]
    const deadheadMiles = (activeAvailableCall.load.originDeadheadMiles?.miles || 0) + (activeAvailableCall.load.destinationDeadheadMiles?.miles || 0)

    const onToggleMuteButtonClicked = (event: React.MouseEvent) => {
        event.stopPropagation()
        onToggleMute()
    }

    const onEndCallButtonClicked = (event: React.MouseEvent) => {
        event.stopPropagation()
        onEndCall()
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                zIndex: 900 - index,
                right: WIDGET_SPACING,
                bottom: START_BOTTOM + index * (WIDGET_HEIGHT + WIDGET_SPACING),
                // Add stacking animation when multiple calls
                transition: 'all 0.3s ease-in-out',
                transform: activeCallsCount > 1 ? 'scale(0.95)' : 'scale(1)',
            }}
        >
            <Box
                onClick={onMaximize}
                sx={{
                    width: '600px',
                    minWidth: '480px',
                    maxWidth: '700px',
                    bgcolor: 'background.surface',
                    borderRadius: 'md',
                    boxShadow: 'sm', // Reduced shadow
                    border: '2px solid',
                    borderColor: 'divider',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 'md',
                        // Slightly intensify background on hover - only for rate and dispatcher
                    },
                    ...(isTransitioning && {
                        opacity: 0,
                        transition: 'opacity 0.2s ease-out',
                    }),
                    // Initial entry animation
                    '@keyframes slideInFromBottom': {
                        from: {
                            transform: 'translateY(100%)',
                            opacity: 0,
                        },
                        to: {
                            transform: 'translateY(0)',
                            opacity: 1,
                        },
                    },
                    animation: 'slideInFromBottom 0.3s ease-out',
                }}
            >
                {/* Header with call type and call status indicator */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: getStatusColor(activeAvailableCall.call?.status),
                                animation: 'pulseDot 1.5s infinite',
                                '@keyframes pulseDot': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.4 },
                                    '100%': { opacity: 1 },
                                },
                            }}
                        />
                        <Chip
                            size="sm"
                            color={activeAvailableCall.call?.status === CallStatus.CONNECTED ? 'success' : 'primary'}
                            sx={{ fontSize: '0.7rem', height: '20px', minHeight: '20px' }}
                        >
                            Call {activeAvailableCall.call?.status === CallStatus.INITIATING ? '(Connecting...)' : ''}
                        </Chip>

                        <CallDurationTimer startedAt={activeAvailableCall.call?.startedAt} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            level="body-sm"
                            sx={{
                                ml: 'auto',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                            }}
                        >
                            {activeAvailableCall.phoneNumber}
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="plain"
                            onClick={onMaximize}
                            sx={{
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        >
                            <OpenInFull fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Load Information Section - Single Line Design */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'background.level1', // Default for everything else
                        p: 1,
                        borderRadius: 'sm',
                        overflow: 'hidden',
                        flexWrap: 'nowrap',
                        minHeight: '36px',
                        border: '1px solid',
                        borderColor: 'transparent', // Default for everything else
                    }}
                >
                    {/* Rate Information - Compact */}
                    {activeAvailableCall.load.rateInfo?.rateUsd ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Chip
                                size="sm"
                                color={activeAvailableCall.load.rateInfo.isAIIdentified ? 'success' : 'primary'}
                                variant="soft"
                                sx={{
                                    fontSize: '1rem',
                                    height: '20px',
                                    minHeight: '20px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.3,
                                    px: 0.8,
                                    py: 0.5,
                                }}
                            >
                                {formatCurrency(activeAvailableCall.load.rateInfo.rateUsd)}
                                {activeAvailableCall.load.rateInfo.isAIIdentified && <AutoAwesome sx={{ fontSize: '1rem' }} />}
                            </Chip>
                            {/* Rate per mile - inline */}
                            {activeAvailableCall.load.tripLength && (
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        color: 'primary',
                                        fontSize: '0.8rem',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    ({formatRatePerMile(activeAvailableCall.load.rateInfo.rateUsd, activeAvailableCall.load.tripLength)})
                                </Typography>
                            )}
                        </Box>
                    ) : null}
                    {/* Spacer */}
                    <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', flexShrink: 0 }} />

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Route - Compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Route fontSize="small" color="primary" />
                        <Typography level="body-sm" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatLoadLocation(activeAvailableCall.load.origin)}
                        </Typography>
                        <HorizontalDirectionIcon />
                        <Typography level="body-sm" sx={{ fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatLoadLocation(activeAvailableCall.load.destination)}
                        </Typography>
                    </Box>

                    {/* Separator */}
                    <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider', flexShrink: 0 }} />

                    {/* Trip Details - Compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {activeAvailableCall.load.tripLength && (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="primary"
                                sx={{
                                    fontSize: '0.8rem',
                                    height: '18px',
                                    minHeight: '18px',
                                    fontWeight: 'medium',
                                    px: 1,
                                }}
                            >
                                {activeAvailableCall.load.tripLength}mi
                            </Chip>
                        )}
                        {deadheadMiles > 0 && (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="warning"
                                sx={{
                                    fontSize: '0.7rem',
                                    height: '16px',
                                    minHeight: '16px',
                                    px: 1,
                                }}
                            >
                                +{deadheadMiles}DH
                            </Chip>
                        )}
                    </Box>
                </Box>

                {/* Control buttons */}
                <Box sx={{ display: 'flex', gap: 1, pt: 0 }}>
                    <Button
                        size="sm"
                        color={muted ? 'warning' : 'primary'}
                        variant="soft"
                        startDecorator={muted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
                        onClick={onToggleMuteButtonClicked}
                        sx={{
                            flex: 1,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' },
                            ...(muted && {
                                animation: 'mutePulse 2s infinite',
                                '@keyframes mutePulse': {
                                    '0%, 100%': {
                                        bgcolor: 'warning.50',
                                    },
                                    '50%': {
                                        bgcolor: 'warning.100',
                                    },
                                },
                            }),
                        }}
                    >
                        {muted ? 'Unmute' : 'Mute'}
                    </Button>

                    {/* End Call button - same for both types */}
                    <Button
                        size="sm"
                        color="danger"
                        variant="soft"
                        startDecorator={<CallEnd fontSize="small" />}
                        onClick={onEndCallButtonClicked}
                        loading={endingCall}
                        sx={{
                            flex: 1,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' },
                        }}
                    >
                        End
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
